import React, { useEffect } from 'react';
import SEO, { OGType } from '../components/head/SEO';
import Breadcrumbs from '../components/head/Breadcrumbs';
import { navbarLayout } from '../layouts/navbarLayout';
import {
  CmsContentPage,
  CmsDestination,
  CmsMemory
} from '../integrations/contentful/types';
import ContentBlockList from '../components/content-blocks/ContentBlockList';
import {
  fetchContentPage,
  fetchDestinations,
  fetchMemories
} from '../integrations/contentful/queries';
import { baseXUnit, breakpoints } from '../styles/theme';
import HeroSection from '../components/home/HeroSection';
import TripsGrid from '../components/home/TripsGrid';
import CoursesGrid from '../components/home/CoursesGrid';
import WhyGolf from '../components/home/WhyGolf';

import { TimPage } from './TimPage';

interface HomePageProps {
  contentPage?: CmsContentPage;
  memories: CmsMemory[];
  destinations: CmsDestination[];
}

const HomePage: TimPage<HomePageProps> = ({
  contentPage,
  memories,
  destinations
}) => {
  // Add a body class while on the homepage so we can style the fixed navbar uniquely
  useEffect(() => {
    document.body.classList.add('homepage');
    return () => {
      document.body.classList.remove('homepage');
    };
  }, []);

  if (!contentPage) {
    return null;
  }

  return (
    <div data-cy="home-page">
      <SEO
        title={contentPage.fields.seoTitle || contentPage.fields.title}
        canonicalPath="/"
        type={OGType.website}
        description={
          contentPage.fields.seoDescription ||
          contentPage.fields.leadParagraph ||
          ''
        }
      />
      <Breadcrumbs />

      <div className="tim-section">
        <HeroSection />

        <WhyGolf />

        <TripsGrid memories={memories} />

        <CoursesGrid destinations={destinations} />
      </div>

      <div className="block-list-shadow" />
      <div className="block-list-wrapper">
        {contentPage.fields.blocks && (
          <ContentBlockList blocks={contentPage.fields.blocks} />
        )}
      </div>

      {/*language=CSS*/}
      <style jsx global>{`
        /* Pull hero section up under the fixed navbar on the homepage */
        .tim-section {
          margin-top: -${baseXUnit(8)}; /* defaultNavbarHeight is 8 */
        }
        @media ${breakpoints.medium} {
          .tim-section {
            margin-top: -${baseXUnit(13)}; /* DefaultNavbar has top={[8, 13, 13, 13]} */
          }
        }

        /* Remove the white navbar background gradient and make it non-sticky on the homepage */
        body.homepage .default-nav-bar {
          background-image: none;
          background: transparent;
          position: absolute;
        }

        /* White navbar text only on the homepage */
        body.homepage .default-nav-bar .h4,
        body.homepage .default-nav-bar h4,
        body.homepage .default-nav-bar [data-testid='text-menu-element'],
        body.homepage .default-nav-bar .name,
        body.homepage .default-nav-bar .tim-logo {
          color: #fff;
        }

        /* Icon strokes/fills in nav on homepage */
        body.homepage .default-nav-bar svg path {
          stroke: #fff;
          fill: #fff;
        }
      `}</style>
      {/*language=CSS*/}
      <style jsx>{`
        .block-list-shadow {
          background: #fff;
          box-shadow: 0 -2px 15px 0 rgba(0, 0, 0, 0.15);
          border-radius: ${baseXUnit(2)};
          margin-top: -${baseXUnit(2.5)};
          margin-bottom: -${baseXUnit(2.5)};
          height: ${baseXUnit(5)};
          position: relative;
          z-index: 0;
        }

        .block-list-wrapper {
          position: relative;
          z-index: 1;
          background: #fff;
          padding-top: ${baseXUnit(12)};
        }

        @media ${breakpoints.medium} {
          .block-list-shadow {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

HomePage.getLayout = (page) => navbarLayout(null, page);

HomePage.getInitialProps = async function () {
  const [contentPage, memories, destinations] = await Promise.all([
    fetchContentPage('home'),
    fetchMemories(undefined, 4), // Fetch top 4 memories
    fetchDestinations() // Fetch destinations, we slice in component but good to have data
  ]);

  const httpErrorCode = !contentPage ? 404 : undefined;
  return {
    contentPage,
    memories,
    destinations: destinations || [],
    httpErrorCode
  };
};

export default HomePage;
